import { useEffect, useState, Fragment, useMemo, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import LinkCard from './LinkCard'
import NewProductCard from './NewProductCard'
import RegisterProductModern from '../RegisterProductModern'
import SimpleButton from '../common/SimpleButton'
import Breadcrumb from '../common/Breadcrumb'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import VideoModal from '../common/VideoModal'
import { useWindowSize } from '../../libs/hooks'
import { Pages } from '../../utils/pages'
import { getDashboardHowToVideos } from '../../utils/functions'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import { currentDay } from '../../utils/filters/date'
import { hasCorporateAdminModulesPermission, hasInstrumentTrackingPermission } from '../../utils/permissionValidation'
import { getVideosList } from '../../utils/requests/videosAPI'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { COLTENE_BRANCH_UUID, COLTENE_BRANCHES } from '../../actions/root'
import { useAppContext } from '../../libs/contextLib'
import { VideoPlayIcon } from '../../icons'

const dashboardVideosCache = new Map()
const dashboardVideosInFlight = new Map()

function DentistDashboard(props) {
  const isMobile = useWindowSize()[0] <= 768
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { showFeatureDev } = useAppContext()
  const { linkCards } = props
  const [cardsData, setCardsData] = useState([])
  const [videos, setVideos] = useState([])
  const [userPermissions, setUserPermissions] = useState([])
  const [showRegisterProduct, setShowRegisterProduct] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [branchName, setBranchName] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [canRegisterProduct, setCanRegisterProduct] = useState(false)
  
  useEffect(() => {
    async function fetchData() {
      let groupedProducts = []

      if (Object.keys(props.userProducts).length > 0) {
        groupedProducts = Object.values(props.userProducts)?.reduce((acc, userProduct) => {
          const model = userProduct.model

          if (!acc[model]) {
            acc[model] = []
          }

          acc[model].push(userProduct)

          return acc
        }, [])

        groupedProducts?.sort((a, b) => {
          return a.model?.toLowerCase()?.localeCompare(b.model?.toLowerCase())
        })
      }

      if (props.apertures?.length > 0 && showFeatureDev) {
        const groupedAperturesByType = props.apertures.reduce((acc, aperture) => {
          if (aperture.devices?.length > 0) {
            const type = aperture.type

            if (!acc[type]) {
              acc[type] = []
            }

            acc[type].push(
              ...(aperture.devices.map((device) => {
                return {
                  model: aperture.type,
                  model_id: device.model_id,
                  serial_number: device.serial_number,
                  unit_nickname: device.nickname,
                }
              }) || []),
            )
          }

          return acc
        }, [])

        if (hasInstrumentTrackingPermission(props.userPermissions)) {
          groupedProducts = { ...groupedProducts, ...groupedAperturesByType }
        }
      }

      setCardsData(groupedProducts)
    }

    fetchData()
  }, [props.apertures, props.userProducts, props.userPermissions])

  useEffect(() => {
    const isCorporateAdmin = hasCorporateAdminModulesPermission(props.userPermissions)
    if (isCorporateAdmin) {
      const colteneBranchId = localStorage.getItem(COLTENE_BRANCH_UUID)
      const colteneBranches = JSON.parse(localStorage.getItem(COLTENE_BRANCHES) || '[]')

      const branch = colteneBranches.find((branch) => branch.branchUuid === colteneBranchId)
      if (branch) {
        setBranchName(branch.branchName)
      }

      const isBranchAdmin = props.userBranches?.find((branch) => branch.branchUuid === colteneBranchId)?.email === props.authedUser?.email

      if (isBranchAdmin) {
        setCanRegisterProduct(true)
      }

      return
    }

    setCanRegisterProduct(true)
  }, [props.userPermissions, props.userBranches])

  useEffect(() => {
    const getVideos = async () => {
      const userKey = props.authedUser?.cognitoSub || 'anonymous'
      setShowSpinner(true)

      try {
        if (dashboardVideosCache.has(userKey)) {
          setVideos(dashboardVideosCache.get(userKey) || [])
          return
        }

        let request = dashboardVideosInFlight.get(userKey)
        if (!request) {
          request = getVideosList()
            .then((videosData) => {
              const list = Array.isArray(videosData?.data) ? videosData.data : []
              dashboardVideosCache.set(userKey, list)
              return list
            })
            .finally(() => {
              dashboardVideosInFlight.delete(userKey)
            })
          dashboardVideosInFlight.set(userKey, request)
        }

        const videosData = await request

        setVideos(videosData || [])
      } finally {
        setShowSpinner(false)
      }
    }

    getVideos()
  }, [props.authedUser?.cognitoSub])

  useEffect(() => {
    setShowSpinner(props.productsLoading)
  }, [props.productsLoading])

  useEffect(() => {
    const permissionArr = props.userPermissions ? (Array.isArray(props.userPermissions) ? props.userPermissions : Object.values(props.userPermissions)) : []
    setUserPermissions(permissionArr)
  }, [props.userPermissions])

  useLayoutEffect(() => {
    setSelectedLanguage(i18n.language?.split('-')[0]?.toUpperCase() || 'EN')
  }, [i18n.language])

  const dashboardVideos = useMemo(() => {
    return getDashboardHowToVideos(videos, selectedLanguage)
  }, [videos, selectedLanguage])

  const handleViewProductsClick = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.productPage,
      label: 'Dashboard Page: View my Products',
    })

    history.push({ pathname: Pages.myProducts.route })
  }

  const openVideoModal = (item) => {
    setSelectedVideo(item)
  }

  const closeVideoModal = () => {
    setSelectedVideo(null)
  }

  return (
    <div className="dentist-dashboard-wrapper">
      <Breadcrumb path={window.location.pathname} />
      <div className={`dashboard-title d-flex ${isMobile ? 'flex-column' : 'flex-row'} flex-justify-between`}>
        <h1>{branchName || t('dashboard.welcome', { firstName: props.authedUser?.firstname, lastName: props.authedUser?.lastname })}</h1>
        <div>{currentDay()}</div>
      </div>
      <div className={`dashboard-content d-flex ${isMobile ? 'flex-column' : 'flex-row'} flex-justify-between`}>
        <div className={`links-wrapper d-flex ${isMobile ? 'with-order-2 flex-column' : 'flex-row'}`}>
          {linkCards.map((card, index) => (
            <Fragment key={index}>{userPermissions.includes(card.permission) && <LinkCard cardContent={card} />}</Fragment>
          ))}
        </div>
        <div className={`products-wrapper ${isMobile ? 'with-order-1' : ''}`}>
          <div className="products-title">{t('dashboard.your_products')}</div>
          <div className="products-list d-flex flex-column">
            {!showSpinner &&
              Object.entries(cardsData)
                .sort()
                .map(([model, machines], index) => {
                  return (
                    <NewProductCard
                      key={`${model}-${index}`}
                      isICTrackProduct={showFeatureDev && ['washer', 'sterilizer'].includes(model)}
                      model={model}
                      machines={machines}
                    />
                  )
                })}
            {!showSpinner && Object.keys(cardsData).length === 0 && <EmptyPageResults title={t('dashboard.no-products-found')} />}
          </div>
          <div className="products-actions d-flex flex-justify-between">
            {canRegisterProduct && (
              <SimpleButton onClick={() => setShowRegisterProduct(true)} className="cancel-button">
                {t('dashboard.register_product')}
              </SimpleButton>
            )}
            <SimpleButton className="submit-button rightmost-button" onClick={handleViewProductsClick}>
              {t('dashboard.view_products')}
            </SimpleButton>
          </div>
        </div>
      </div>
      {showRegisterProduct && <RegisterProductModern closeModal={() => setShowRegisterProduct(false)} />}
      {dashboardVideos.length > 0 && (
        <div className="videos-content d-flex flex-column">
          <div className="section-title">{t('nav.videos')}</div>
          <div className="videos-wrapper">
            {dashboardVideos.map((video, index) => (
              <div
                key={index}
                className="video-item-wrapper"
                onClick={() => openVideoModal(video)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => e.key === keyboardEvents.ENTER && openVideoModal(video)}
              >
                <div className="thumbnail-wrapper">
                  <div className="thumbnail-icon">
                    <VideoPlayIcon />
                  </div>
                  <img src={video.thumbnail_url} alt="video" />
                </div>
                <p className="title">{video.title}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedVideo && (
        <VideoModal
          title={selectedVideo.title}
          videoId={selectedVideo.video_id}
          onCloseVideo={closeVideoModal}
          gaAction={gaActions.videoClick}
          gaCategory={gaCategories.dashboardPage}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function mapStateToProps({ authedUser, apertures, userProducts, userPermissions, userCompany }) {
  return {
    authedUser,
    userProducts: userProducts?.products || {},
    productsLoading: userProducts?.loading || false,
    apertures: apertures?.apertures || [],
    userPermissions,
    userBranches: userCompany?.userBranches || [],
  }
}

export default connect(mapStateToProps)(DentistDashboard)

